import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { isDeadAuthLinkUrl } from '@/lib/auth-callback'
import { useRecoverySession } from '@/hooks/use-recovery-session'
import { testIds } from '@/lib/test-ids'
import { AuthLayout } from './auth-layout'
import { AuthFormAlert } from './auth-form-alert'
import { ExpiredInviteForm } from './expired-invite-form'
import { Button } from '@/components/ui/button'
import { PasswordInput } from './password-input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'

const resetSchema = z
  .object({
    password: z.string().min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

type ResetFormValues = z.infer<typeof resetSchema>

export function ResetPasswordForm() {
  const navigate = useNavigate()
  const [generalError, setGeneralError] = useState<string | null>(null)
  const { ready: sessionReady, checking, error: sessionError } = useRecoverySession(supabase)

  const form = useForm<ResetFormValues>({
    resolver: zodResolver(resetSchema),
    defaultValues: { password: '', confirmPassword: '' },
  })

  const onSubmit = async (values: ResetFormValues) => {
    if (!supabase) return
    if (!sessionReady) {
      setGeneralError(sessionError ?? 'Auth session missing!')
      return
    }
    setGeneralError(null)
    const { error } = await supabase.auth.updateUser({
      password: values.password,
      data: { password_set_at: new Date().toISOString() },
    })
    if (error) {
      setGeneralError(error.message)
      return
    }
    window.dispatchEvent(new CustomEvent('flownotes:password-set'))
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (session) {
      navigate('/', { replace: true })
      return
    }
    navigate('/login?message=Password updated. You can log in now.', {
      replace: true,
    })
  }

  if (isDeadAuthLinkUrl()) {
    return <ExpiredInviteForm />
  }

  if (checking) {
    return (
      <AuthLayout title="Set your password">
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Confirming your invite link...</p>
        </div>
      </AuthLayout>
    )
  }

  if (!sessionReady) {
    return <ExpiredInviteForm />
  }

  return (
    <AuthLayout
      title="Set your password"
      description="Choose a password so you can sign in again after this session."
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
          data-testid={testIds.auth.resetPasswordForm}
        >
          {generalError && <AuthFormAlert message={generalError} />}
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>New password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="********"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Confirm password</FormLabel>
                <FormControl>
                  <PasswordInput
                    placeholder="********"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              'Set password'
            )}
          </Button>
        </form>
      </Form>
    </AuthLayout>
  )
}
